import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  isValidEmail,
  isValidPassword,
  strToHtml,
  toE164Kr,
} from "../helpers/utils";
import { REST_API_URL } from "../constants";
import API from "../service/API";
import { modal } from "../modal/ModalService";
import _ from "lodash";
import { useLang } from "../context/LangProvider";
import CountryDropDown from "../components/CountryDropDown";

const TopBar = ({ onBack, step, i18n }) => {
  return (
    <header
      className="topbar"
      style={{ width: "380px", margin: "0 auto", padding: "0 10px" }}
    >
      <div className="topbar__inner">
        <button className="topbar__back" onClick={onBack} aria-label="뒤로가기">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">
            {step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep")}
          </div>
        </button>
      </div>
    </header>
  );
};

const IdStepRender = React.memo(
  ({
    step,
    onAuthoize,
    onPasswordTypeChange,
    account,
    onSigninGo,
    onAuthorizeAgain,
    onSignup,
    i18n,
    lang,
    countryCode,
    onCountrySelect,
    phone,
    setPhone,
    setCodeSent,
    onSendPhoneAuthCode,
    phoneAuthCode,
    isSending,
    setPhoneAuthCode,
    onConfrimPhoneAuthCode,
  }) => {
    switch (step) {
      case 1:
        return lang === "ko" ? (
          <>
            <div className="h4 bold">{i18n("find.id.title")}</div>
            <p>
              {i18n("find.id.desc.1")}
              <br />
              {i18n("find.id.desc.2")}
            </p>
            <form className="login__form" style={{ marginTop: "30px" }}>
              <button
                type="button"
                onClick={onAuthoize}
                className="login__btn login__btn--primary"
                style={{ marginTop: "80px" }}
              >
                {i18n("common.verifyPhone")}
              </button>
            </form>
          </>
        ) : (
          <div className="signup-2">
            <div className="progress-bar">
              <div
                className="progress"
                style={{
                  width: "33.33%",
                  position: "absolute",
                  top: 0,
                  left: "66.66%",
                }}
              />
            </div>
            <div className="flex-column" style={{ gap: "15px" }}>
              <div className="h4 bold">Please verify your mobile phone.</div>
              <CountryDropDown onCountrySelect={onCountrySelect} />
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                {countryCode ? `+${countryCode}` : null}
                <input
                  id="phone"
                  type="text"
                  pattern="[0-9]*"
                  className="login__input"
                  style={{ flex: 1 }}
                  placeholder="Enter phone number (numbers only)"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCodeSent(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={onSendPhoneAuthCode}
                disabled={!phone || isSending}
                aria-disabled={!phone || isSending}
                className="login__btn login__btn--primary"
              >
                Request verification code
              </button>
            </div>
            <div
              className="flex-column"
              style={{ gap: "15px", marginTop: "50px" }}
            >
              <div>
                <div className="login__label h6">Verification code</div>
                <input
                  id="phoneAuthCode"
                  type="text"
                  className="login__input"
                  placeholder="6-digit"
                  value={phoneAuthCode}
                  onChange={(e) => setPhoneAuthCode(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={!phone || !phoneAuthCode || isSending}
                aria-disabled={!phone || !phoneAuthCode || isSending}
                onClick={onConfrimPhoneAuthCode}
                className="login__btn login__btn--primary"
              >
                Next
              </button>
            </div>
          </div>
        );
      case 2: {
        let isSocial = account?.["custom:provider"];
        const socialName =
          account?.["custom:provider"] === "KAKAO" ? "카카오" : "네이버";
        return account ? (
          <>
            <div className="h4 bold">{i18n("find.id.found.title")}</div>
            <div className="account-box">
              <div className="h6 bold brownDark70-color">
                {account?.email || ""}
              </div>
              {!isSocial && account?.["custom:createdAt"] ? (
                <div className="h7 brownDark70-color">
                  {moment(account?.["custom:createdAt"]).format("YYYY.MM.DD")}{" "}
                  {i18n("existing.joined")}
                </div>
              ) : null}
              {isSocial ? (
                <div className="h7 brownDark70-color">
                  {`SNS 계정(${socialName})으로 인증된 아이디입니다.`}
                </div>
              ) : null}
            </div>

            {isSocial ? null : (
              <button
                className="topbar__forward"
                onClick={onPasswordTypeChange}
                aria-label={i18n("find.id.resetPassword.aria")}
              >
                <span className="topbar__forward-text h6">
                  {i18n("find.id.resetPassword")}
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M9 6l6 6-6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            <button
              onClick={onSigninGo}
              className="login__btn login__btn--primary"
              style={{ marginTop: "80px" }}
            >
              {i18n("signin.cta.login")}
            </button>

            <div
              className="account-box"
              style={{
                gap: "10px",
                alignItems: "flex-start",
                padding: "15px",
              }}
            >
              <div className="tiny brownDark70-color">
                • {i18n("find.id.notice.noResetForSNS")}
              </div>
              <div className="tiny brownDark70-color">
                • {i18n("find.id.notice.useQuickLogin")}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="h4 bold">{i18n("find.id.notFound.title")}</div>
            <p className="brownDark70-color">
              {i18n("find.id.notFound.desc.1")}
              <br />
              {i18n("find.id.notFound.desc.2")}
            </p>
            <div
              className="flex-center"
              style={{ gap: "15px", marginTop: "80px" }}
            >
              <button
                onClick={onAuthorizeAgain}
                className="login__btn login__btn--outline"
              >
                {i18n("find.id.notFound.retry")}
              </button>
              <button
                onClick={onSignup}
                className="login__btn login__btn--primary"
              >
                {i18n("signin.cta.signup")}
              </button>
            </div>
            <div
              className="account-box"
              style={{
                alignItems: "flex-start",
                padding: "15px",
              }}
            >
              <div className="h7 brownDark70-color">
                {strToHtml(i18n("find.id.notFound.guide"))}
              </div>
            </div>
          </>
        );
      }

      default:
        return null;
    }
  }
);

const PasswordStepRender = React.memo(
  ({
    step,
    onAuthoize,
    email,
    setEmail,
    password,
    rePassword,
    setPassword,
    setRepassword,
    onPasswordSave,
    busy,
    i18n,
    lang,
    countryCode,
    onCountrySelect,
    phone,
    setPhone,
    setCodeSent,
    onSendPhoneAuthCode,
    phoneAuthCode,
    isSending,
    setPhoneAuthCode,
    onConfrimPhoneAuthCode,
    account,
    onAuthorizeAgain,
    onSignup,
    onSigninGo,
  }) => {
    switch (step) {
      case 1:
        return lang === "ko" ? (
          <>
            <div className="h4 bold">{i18n("find.password.title")}</div>
            <p>
              {i18n("find.password.desc.1")}
              <br />
              {i18n("find.password.desc.2")}
            </p>
            <form className="login__form" style={{ marginTop: "30px" }}>
              <div className="login__label h6" htmlFor="email">
                {i18n("signup.form.email.label")}
              </div>
              <input
                id="email"
                type="email"
                className="login__input"
                placeholder={i18n("signup.form.email.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <button
                type="button"
                onClick={onAuthoize}
                disabled={!isValidEmail(email)}
                aria-disabled={!isValidEmail(email)}
                className="login__btn login__btn--primary"
                style={{ marginTop: "80px" }}
              >
                {i18n("common.verifyPhone")}
              </button>
            </form>
          </>
        ) : (
          <div className="signup-2">
            <div className="progress-bar">
              <div
                className="progress"
                style={{
                  width: "33.33%",
                  position: "absolute",
                  top: 0,
                  left: "66.66%",
                }}
              />
            </div>
            <div className="flex-column" style={{ gap: "15px" }}>
              <div className="h4 bold">Please verify your mobile phone.</div>
              <CountryDropDown onCountrySelect={onCountrySelect} />
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                {countryCode ? `+${countryCode}` : null}
                <input
                  id="phone"
                  type="text"
                  pattern="[0-9]*"
                  className="login__input"
                  style={{ flex: 1 }}
                  placeholder="Enter phone number (numbers only)"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCodeSent(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={onSendPhoneAuthCode}
                disabled={!phone || isSending}
                aria-disabled={!phone || isSending}
                className="login__btn login__btn--primary"
              >
                Request verification code
              </button>
            </div>
            <div
              className="flex-column"
              style={{ gap: "15px", marginTop: "50px" }}
            >
              <div>
                <div className="login__label h6">Verification code</div>
                <input
                  id="phoneAuthCode"
                  type="text"
                  className="login__input"
                  placeholder="6-digit"
                  value={phoneAuthCode}
                  onChange={(e) => setPhoneAuthCode(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={!phone || !phoneAuthCode || isSending}
                aria-disabled={!phone || !phoneAuthCode || isSending}
                onClick={onConfrimPhoneAuthCode}
                className="login__btn login__btn--primary"
              >
                Next
              </button>
            </div>
          </div>
        );
      case 2:
        let isSocial = account?.["custom:provider"];
        const socialName =
          account?.["custom:provider"] === "KAKAO" ? "카카오" : "네이버";
        return account ? (
          <>
            <div className="h4 bold">{i18n("find.password.reset.title")}</div>
            {isSocial ? (
              <>
                <div className="account-box">
                  <div className="h6 bold brownDark70-color">
                    {account?.email || ""}
                  </div>
                  <div className="h7 brownDark70-color">
                    {`SNS 계정(${socialName})으로 인증된 아이디입니다.`}
                  </div>
                </div>
                <div
                  className="flex-center"
                  style={{ gap: "15px", marginTop: "80px" }}
                >
                  <button
                    onClick={onSigninGo}
                    className="login__btn login__btn--primary"
                  >
                    {i18n("signin.cta.login")}
                  </button>
                </div>

                <div
                  className="account-box"
                  style={{
                    gap: "10px",
                    alignItems: "flex-start",
                    padding: "15px",
                  }}
                >
                  <div className="tiny brownDark70-color">
                    • {i18n("find.id.notice.noResetForSNS")}
                  </div>
                  <div className="tiny brownDark70-color">
                    • {i18n("find.id.notice.useQuickLogin")}
                  </div>
                </div>
              </>
            ) : (
              <>
                <p>
                  {i18n("find.password.reset.desc.1")}
                  <br />
                  {i18n("find.password.reset.desc.2")}
                </p>
                <form className="login__form" style={{ marginTop: "30px" }}>
                  <div className="login__label h6" htmlFor="password">
                    {i18n("signup.form.password.label")}
                  </div>
                  <input
                    id="password"
                    type="password"
                    className="login__input"
                    placeholder={i18n("signup.form.password.placeholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <input
                    id="rePassword"
                    type="password"
                    className="login__input"
                    placeholder={i18n(
                      "signup.form.passwordConfirm.placeholder"
                    )}
                    value={rePassword}
                    onChange={(e) => setRepassword(e.target.value)}
                    style={{ marginTop: "15px" }}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={onPasswordSave}
                    disabled={
                      !isValidPassword(password) ||
                      password !== rePassword ||
                      busy
                    }
                    aria-disabled={
                      !isValidPassword(password) ||
                      password !== rePassword ||
                      busy
                    }
                    className="login__btn login__btn--primary"
                    style={{ marginTop: "80px" }}
                  >
                    {i18n("find.password.reset.cta")}
                  </button>
                </form>
              </>
            )}
          </>
        ) : (
          <>
            <div className="h4 bold">{i18n("find.id.notFound.title")}</div>
            <p className="brownDark70-color">
              {i18n("find.id.notFound.desc.1")}
              <br />
              {i18n("find.id.notFound.desc.2")}
            </p>
            <div
              className="flex-center"
              style={{ gap: "15px", marginTop: "80px" }}
            >
              <button
                onClick={onAuthorizeAgain}
                className="login__btn login__btn--outline"
              >
                {i18n("find.id.notFound.retry")}
              </button>
              <button
                onClick={onSignup}
                className="login__btn login__btn--primary"
              >
                {i18n("signin.cta.signup")}
              </button>
            </div>
            <div
              className="account-box"
              style={{
                alignItems: "flex-start",
                padding: "15px",
              }}
            >
              <div className="h7 brownDark70-color">
                {strToHtml(i18n("find.id.notFound.guide"))}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  }
);

const FindAccount = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const { i18n, lang } = useLang();
  const [isKorea, setIsKorea] = useState(true);
  const typeRef = useRef(sp.get("type"));
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [rePassword, setRepassword] = useState("");
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isSending, setSending] = useState(false);
  const [veifyCodeSent, setCodeSent] = useState(false);
  const [phoneAuthCode, setPhoneAuthCode] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const country = await API.getCountry();
        setIsKorea(country.isKorea ?? true);
      } catch (err) {
        console.error("Failed to load country", err);
      }
    })();
  }, []);

  const handleBackPress = () => {
    if (step === 1) {
      window.history.back();
    } else {
      setStep(step - 1);
    }
  };

  const handleAuthorize = async () => {
    window.MOBILEOK?.process(`${REST_API_URL}/mok/request`, "WB", "result");
  };

  const handlePasswordSave = async () => {
    setBusy(true);
    if (account?.sub) {
      await API.updatePassword(account.sub, password)
        .then(async () => {
          const ok = await modal.alert({
            message: i18n("find.password.reset.success"),
            okText: i18n("common.ok"),
            backdropClose: false,
          });
          if (ok) nav("/");
          setBusy(false);
        })
        .catch((e) => {
          setBusy(false);
          modal.alert({
            message: i18n("find.password.reset.fail"),
            okText: i18n("common.ok"),
            backdropClose: true,
          });
        });
    } else {
      setBusy(false);
    }
  };

  const handlePasswordTypeChange = () => {
    setStep(1);
    setPhoneAuthCode("");
    typeRef.current = "password";
  };

  const handleSignInGo = () => window.history.back();
  const handleAuthorizeAgain = () => setStep(1);
  const handleSignup = () => nav("/signup");

  const checkUser = async (data) => {
    try {
      const userPhone = toE164Kr(data.phone);
      const u = await API.checkUser(null, userPhone, null, null);
      if (!u) {
        await modal.alert({
          message: i18n("find.common.noSignup"),
          okText: i18n("common.ok"),
          backdropClose: true,
        });
      } else {
        setStep(2);
        setAccount(u.attributes || {});
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleConfrmPhoneAuthCode = async () => {
    try {
      const isValid = await API.confirmPhoneAuthCode(
        phoneAuthCode,
        phone,
        countryCode
      );
      if (isValid) {
        try {
          const userPhone = `+${countryCode}${phone}`;
          const u = await API.checkUser(null, userPhone, null, null);
          if (!u) {
            await modal.alert({
              message: i18n("find.common.noSignup"),
              okText: i18n("common.ok"),
              backdropClose: true,
            });
          } else {
            setStep(2);
            setAccount(u.attributes || {});
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        modal.alert({
          message: "The verification code is incorrect.",
          okText: "OK",
          backdropClose: true,
        });
      }
    } catch (err) {
      modal.alert({
        message: "Something wrong... Please try again.",
        okText: "OK",
        backdropClose: true,
      });
    }
  };

  const handleCountrySelect = (country) => {
    setCountryCode(country.dialCode);
  };

  const handleSendPhoneAuthCode = async () => {
    try {
      setSending(true);
      await API.sendPhoneAuthCode(phone, countryCode);
      setCodeSent(true);
      setSending(false);
    } catch (err) {
      setSending(false);
    }
  };

  useEffect(() => {
    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {
          checkUser(json);
          if (json) {
            if (typeRef.current === "password") {
              setStep(2);
            } else {
            }
          }
        });
    };

    return () => {
      delete window.result;
    };
  }, []);

  return (
    <div className="signup">
      <TopBar onBack={handleBackPress} step={step} i18n={i18n} />
      <div className="find-account">
        {typeRef.current === "password" ? (
          <PasswordStepRender
            step={step}
            email={email}
            account={account}
            setEmail={setEmail}
            onAuthoize={handleAuthorize}
            password={password}
            rePassword={rePassword}
            setRepassword={setRepassword}
            setPassword={setPassword}
            onPasswordSave={handlePasswordSave}
            busy={busy}
            i18n={i18n}
            lang={lang}
            countryCode={countryCode}
            onCountrySelect={handleCountrySelect}
            phone={phone}
            setPhone={setPhone}
            setCodeSent={setCodeSent}
            onSendPhoneAuthCode={handleSendPhoneAuthCode}
            isSending={isSending}
            phoneAuthCode={phoneAuthCode}
            setPhoneAuthCode={setPhoneAuthCode}
            onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
            onSignup={handleSignup}
            onSigninGo={handleSignInGo}
            onAuthorizeAgain={handleAuthorizeAgain}
          />
        ) : (
          <IdStepRender
            step={step}
            onAuthoize={handleAuthorize}
            account={account}
            onPasswordTypeChange={handlePasswordTypeChange}
            onSigninGo={handleSignInGo}
            onAuthorizeAgain={handleAuthorizeAgain}
            onSignup={handleSignup}
            i18n={i18n}
            lang={lang}
            countryCode={countryCode}
            onCountrySelect={handleCountrySelect}
            phone={phone}
            setPhone={setPhone}
            setCodeSent={setCodeSent}
            onSendPhoneAuthCode={handleSendPhoneAuthCode}
            isSending={isSending}
            phoneAuthCode={phoneAuthCode}
            setPhoneAuthCode={setPhoneAuthCode}
            onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
          />
        )}
      </div>
    </div>
  );
};

export default FindAccount;
